import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { sceneManager } from '../../../services/SceneManager';
import { EnvironmentModel } from '../../views/StoreBuilderDetailsView/environments';
import { MAX_ZOOM, MIN_ZOOM } from './constants';
import { ThreeRefs } from './types';

export const setupThreeScene = (
  mountElement: HTMLDivElement,
  refs: ThreeRefs,
  environment: EnvironmentModel
): (() => void) => {
  // Use the new SceneManager service
  return sceneManager.initializeScene(mountElement, refs, environment);
};

// Legacy implementation (keeping for reference/fallback)
export const setupThreeSceneLegacy = (
  mountElement: HTMLDivElement,
  refs: ThreeRefs,
  environment: EnvironmentModel
): (() => void) => {
  // Scene setup
  const scene = new THREE.Scene();
  refs.scene.current = scene;
  scene.background = new THREE.Color(0xf0f0f0);

  // Calculate appropriate frustum size based on container dimensions
  const containerAspect = mountElement.clientWidth / mountElement.clientHeight;
  const frustumSize = 15; // Controls the visible area size

  // Camera setup - using orthographic camera
  const camera = new THREE.OrthographicCamera(
    (frustumSize * containerAspect) / -2, // left
    (frustumSize * containerAspect) / 2, // right
    frustumSize / 2, // top
    frustumSize / -2, // bottom
    0.1, // near
    1000 // far
  );

  refs.camera.current = camera;
  camera.position.set(0, 10, 0);
  camera.lookAt(0, 0, 0);
  camera.zoom = 2; // Set initial zoom value
  camera.updateProjectionMatrix();

  // Renderer setup
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true,
    powerPreference: 'high-performance',
    precision: 'highp',
    alpha: true,
  });
  refs.renderer.current = renderer;
  renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  mountElement.appendChild(renderer.domElement);

  // Controls setup
  const controls = new OrbitControls(camera, renderer.domElement);
  refs.controls.current = controls;
  controls.enableDamping = false;
  controls.enableRotate = false;
  controls.maxPolarAngle = Math.PI / 2; // Limit to horizontal view
  controls.minPolarAngle = 0; // Allow top-down view
  controls.enablePan = true;
  controls.enableZoom = true;
  controls.panSpeed = 0.8;
  controls.zoomSpeed = 0.8;
  controls.minDistance = 2;
  controls.maxDistance = 15;
  controls.minZoom = MIN_ZOOM; // Set minimum zoom level
  controls.maxZoom = MAX_ZOOM; // Set maximum zoom level
  controls.keyPanSpeed = 0.8;
  controls.target.set(0, 0, 0);
  controls.update();

  // Add floor plane with environment image
  // Calculate aspect ratio of environment image to maintain proportions
  const envWidth = environment.width;
  const envHeight = environment.height;
  const imageAspect = envWidth / envHeight;

  const planeWidth = 15;
  const planeHeight = planeWidth / imageAspect;
  const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);

  const textureLoader = new THREE.TextureLoader();
  const texture = textureLoader.load(
    environment.renders.renderedWithGrid,
    (loadedTexture) => {
      loadedTexture.minFilter = THREE.LinearFilter;
      loadedTexture.magFilter = THREE.LinearFilter;
      loadedTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      loadedTexture.generateMipmaps = false;
      loadedTexture.needsUpdate = true;

      material.needsUpdate = true;
    }
  );

  texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
    depthTest: true,
  });

  const plane = new THREE.Mesh(geometry, material);
  plane.rotation.x = -Math.PI / 2;
  plane.userData.isInteractive = false;
  plane.userData.isFloorPlane = true;
  refs.floorPlane.current = plane;
  scene.add(plane);

  refs.gridHelper.current = null;

  // Add lights
  const light = new THREE.DirectionalLight(0xffffff, 1);
  light.position.set(0, 10, 0);
  scene.add(light);
  scene.add(new THREE.AmbientLight(0xffffff, 5));

  renderer.setPixelRatio(window.devicePixelRatio);

  const animate = () => {
    requestAnimationFrame(animate);
    if (!refs.scene.current || !refs.camera.current || !refs.renderer.current)
      return;
    if (refs.controls.current) refs.controls.current.update();
    refs.renderer.current.render(refs.scene.current, refs.camera.current);
  };
  animate();

  const handleResize = () => {
    if (!mountElement || !refs.camera.current || !refs.renderer.current) return;
    const width = mountElement.clientWidth;
    const height = mountElement.clientHeight;

    const newAspect = width / height;
    const orthoCamera = refs.camera.current as THREE.OrthographicCamera;

    orthoCamera.left = (frustumSize * newAspect) / -2;
    orthoCamera.right = (frustumSize * newAspect) / 2;
    orthoCamera.top = frustumSize / 2;
    orthoCamera.bottom = frustumSize / -2;

    orthoCamera.updateProjectionMatrix();
    refs.renderer.current.setSize(width, height);
  };
  window.addEventListener('resize', handleResize);

  return () => {
    window.removeEventListener('resize', handleResize);
    if (mountElement && refs.renderer.current?.domElement) {
      mountElement.removeChild(refs.renderer.current.domElement);
    }
    refs.renderer.current?.dispose();
  };
};
